import React, { useState, useEffect, useRef } from 'react'
import { gsap } from 'gsap/dist/gsap'
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger'
import AnimatedTrailer from './AnimatedTrailer'
import AnimatedCloudTile from './AnimatedCloudTile'
import { TrailerContainer, CloudTileContainer } from './Styles'
import AnimatedFramedTrailer from './AnimatedFramedTrailer'
import { ButtonLinkProps } from '../../core/ButtonLink/ButtonLink'

export type AnimatedTitleTrailerProps = {
  streamingLogoText: string
  trailerFirstText?: string
  trailerSecondText?: string
  trailerSubtitle?: string
  streamingLogo: string
  trailerVideo: string
  trailerVideoFallback: string
  staticBackground: string
  cloudStartingHeader?: string
  cloudEndingHeader?: string
  cloudStartingSmallText?: string
  cloudEndingSmallText?: string
  cloudButtonLink?: string
  cloudButtonText?: string
  cloudTileImages?: string[]
  cloudMobileTileImages?: string[]
  framedTrailerStaticBackground: string
  framedTrailerVideo: string
  framedTrailerVideoFallback: string
  framedTrailerMainText?: string
  framedTrailerSecondaryText?: string
  framedTrailerImageTiles?: string[]
  framedTrailerSmallImageTiles?: string[]
  framedTrailerTileText?: string[]
  framedTrailerTileGenre?: string[]
  mobileImage: string
  mobileImageHeight?: number
  mobileImageWidth?: number
  tileDensityFactor?: number
  pixelBlurAmount?: number
  lowerBoundScaleRange?: number
  upperBoundScaleRange?: number
  blurPercent?: number
  animationScrub?: number
  lowerImage?: string
  tvLowerImageHeight?: number
  tvLowerImageWidth?: number
  videoGradientDarkness?: number
  frameImage?: React.ReactNode
  buttonLinkProps?: ButtonLinkProps
  showHalfAnim?: boolean
}

gsap.registerPlugin(ScrollTrigger)

const AnimatedTitleTrailer = ({
  streamingLogoText,
  streamingLogo,
  trailerFirstText = '',
  trailerSecondText = '',
  trailerSubtitle = '',
  trailerVideo,
  trailerVideoFallback,
  staticBackground,
  cloudStartingHeader = '',
  cloudEndingHeader = '',
  cloudStartingSmallText = '',
  cloudEndingSmallText = '',
  cloudButtonLink = 'https://telus.com',
  cloudButtonText = '',
  cloudTileImages = [''],
  cloudMobileTileImages = [''],
  framedTrailerStaticBackground,
  framedTrailerVideo,
  framedTrailerVideoFallback,
  framedTrailerMainText = '',
  framedTrailerSecondaryText = '',
  framedTrailerImageTiles,
  framedTrailerSmallImageTiles = [],
  framedTrailerTileText = [''],
  framedTrailerTileGenre = [''],
  mobileImage,
  mobileImageHeight,
  mobileImageWidth,
  tileDensityFactor = 1,
  pixelBlurAmount = 3,
  lowerBoundScaleRange = 0.5,
  upperBoundScaleRange = 1.5,
  blurPercent = 0.1,
  animationScrub = 1,
  lowerImage = '',
  tvLowerImageHeight,
  tvLowerImageWidth,
  videoGradientDarkness = 50,
  frameImage = undefined,
  buttonLinkProps = undefined,
  showHalfAnim = true,
}: AnimatedTitleTrailerProps) => {
  const animatedCloudTileRef = useRef(null)
  const [isMobile, setIsMobile] = useState(false)
  const MOBILE_SIZE = 768
  const [tileHeight, setTileHeight] = useState(0)
  const [isReducedMotionOn, setIsReducedMotionOn] = useState(false)

  gsap.registerPlugin(ScrollTrigger)

  useEffect(() => {
    const reducedMotionOn = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    setIsReducedMotionOn(reducedMotionOn)

    const resizeObserver = new ResizeObserver(() => {
      if (window.document.body.clientWidth < MOBILE_SIZE) {
        setIsMobile(true)
      } else {
        setIsMobile(false)
      }
    })

    if (document) {
      resizeObserver.observe(document.body)
    }
  }, [])

  return (
    <>
      <TrailerContainer data-testid={'animated-title-trailer-testid'}>
        <AnimatedTrailer
          streamingLogo={streamingLogo}
          streamingLogoText={streamingLogoText}
          trailerVideo={trailerVideo}
          trailerVideoFallback={trailerVideoFallback}
          trailerFirstText={trailerFirstText}
          trailerSecondText={trailerSecondText}
          trailerSubText={trailerSubtitle}
          staticBackground={staticBackground}
          tileHeight={tileHeight}
          isReducedMotionOn={isReducedMotionOn}
          videoGradientDarkness={videoGradientDarkness}
          buttonLinkProps={buttonLinkProps}
        />
      </TrailerContainer>
      <CloudTileContainer ref={animatedCloudTileRef}>
        <AnimatedCloudTile
          scrollEnd={showHalfAnim ? '-80%' : '-165%'}
          mobileBP={MOBILE_SIZE}
          setTileHeight={setTileHeight}
          tileName={'tileA'}
          scrollTriggerID={'scrollTriggerA'}
          staticRandomizedPos={false}
          startingHeader={cloudStartingHeader}
          endingHeader={cloudEndingHeader}
          startingSmallText={cloudStartingSmallText}
          endingSmallText={cloudEndingSmallText}
          buttonLink={cloudButtonLink}
          buttonText={cloudButtonText}
          cloudTiles={cloudTileImages}
          mobileCloudTiles={cloudMobileTileImages}
          tileDensityFactor={tileDensityFactor}
          pixelBlurAmount={pixelBlurAmount}
          lowerBoundScaleRange={lowerBoundScaleRange}
          upperBoundScaleRange={upperBoundScaleRange}
          blurPercent={blurPercent}
          animationScrub={animationScrub}
          showHalfAnim={showHalfAnim}
        />
        {isReducedMotionOn && (
          <AnimatedCloudTile
            scrollEnd={'-100%'}
            mobileBP={MOBILE_SIZE}
            setTileHeight={setTileHeight}
            tileName={'tileB'}
            scrollTriggerID={'scrollTriggerB'}
            staticRandomizedPos={true}
            startingHeader={cloudStartingHeader}
            endingHeader={cloudEndingHeader}
            startingSmallText={cloudStartingSmallText}
            endingSmallText={cloudEndingSmallText}
            buttonLink={cloudButtonLink}
            buttonText={cloudButtonText}
            cloudTiles={cloudTileImages}
            mobileCloudTiles={cloudMobileTileImages}
            tileDensityFactor={tileDensityFactor}
            pixelBlurAmount={pixelBlurAmount}
            lowerBoundScaleRange={lowerBoundScaleRange}
            upperBoundScaleRange={upperBoundScaleRange}
            blurPercent={blurPercent}
            animationScrub={animationScrub}
            showHalfAnim={showHalfAnim}
          />
        )}
      </CloudTileContainer>
      <AnimatedFramedTrailer
        data-testid="anim-framed"
        isMobile={isMobile}
        isReducedMotionOn={isReducedMotionOn}
        trailerVideo={framedTrailerVideo}
        trailerVideoFallback={framedTrailerVideoFallback}
        staticBackground={framedTrailerStaticBackground}
        mainText={framedTrailerMainText}
        secondaryText={framedTrailerSecondaryText}
        tileImages={framedTrailerImageTiles}
        smallTileImages={framedTrailerSmallImageTiles}
        tileText={framedTrailerTileText}
        tileGenre={framedTrailerTileGenre}
        mobileImage={mobileImage}
        mobileImageHeight={mobileImageHeight}
        mobileImageWidth={mobileImageWidth}
        lowerImage={lowerImage}
        tvLowerImageHeight={tvLowerImageHeight}
        tvLowerImageWidth={tvLowerImageWidth}
        frameImage={frameImage}
      />
    </>
  )
}

export default AnimatedTitleTrailer
